import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { listStrategies, createSession, type Strategy } from "@/api/alpha";
import SimLockOverlay from "@/components/SimLockOverlay";

const API_BASE = (import.meta.env.VITE_API_BASE as string) || "/api";

const fundOptions = [500000, 1000000, 5000000, 10000000];
const boardOptions = ["沪深主板", "创业板", "科创板", "北交所", "不限制"];
const sectorOptions = ["科技半导体", "新能源", "消费零售", "医药生物", "金融银行", "军工航天", "房地产", "不限制"];
const riskOptions = [
  { value: "conservative", label: "稳健", color: "#10B981", description: "以防御性配置为主，追求稳定收益，回撤控制在-8%以内" },
  { value: "balanced", label: "平衡", color: "#2563EB", description: "攻守兼备，行业均衡配置，在可控风险下追求适度超额收益" },
  { value: "aggressive", label: "激进", color: "#E11D48", description: "趋势跟踪为主，快进快出，追求高收益，可承受较大回撤" },
];

const scanSteps = ["初始资金 ✓", "板块配置 ✓", "风险评估 ✓", "AI 引擎 ✓", "风控系统 ✓"];

interface SimStatus {
  userId: number;
  addonActive: boolean;
  addonExpired: boolean;
}

type RiskLabel = "稳健" | "平衡" | "激进";
function riskOf(maxDrawdown: number): RiskLabel {
  const dd = Math.abs(maxDrawdown);
  if (dd <= 10) return "稳健";
  if (dd <= 18) return "平衡";
  return "激进";
}

export default function SetupPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("alpha_token") || localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;

  const [step, setStep] = useState(1);
  const [fundAmount, setFundAmount] = useState(0);
  const [customFund, setCustomFund] = useState("");
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [riskLevel, setRiskLevel] = useState("balanced");
  const [knowledgeLevel, setKnowledgeLevel] = useState("intermediate");
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(0);
  const [simStatus, setSimStatus] = useState<SimStatus | null>(null);
  const [showLock, setShowLock] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createdOk, setCreatedOk] = useState(false);

  useEffect(() => {
    let alive = true;
    listStrategies()
      .then((res) => { if (alive) setStrategies(res.strategies); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/user/sim-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (alive && j.success && j.data) setSimStatus(j.data);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);

  const saveProfile = useCallback(async () => {
    const response = await fetch(`${API_BASE}/max/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        riskLevel,
        sectors: selectedSectors.join(","),
        knowledgeLevel,
      }),
    });
    if (!response.ok) throw new Error("投资偏好保存失败，请稍后重试");
  }, [knowledgeLevel, riskLevel, selectedSectors, token]);

  const runCreate = useCallback(async () => {
    setCreateError("");
    setCreatedOk(false);
    try {
      let list = strategies;
      if (list.length === 0) {
        const res = await listStrategies();
        list = res.strategies;
        setStrategies(list);
      }
      if (list.length === 0) throw new Error("暂无可用策略，无法创建模拟盘");

      const matched = list.find((s) => s.id === selectedStrategyId);
      if (!matched) throw new Error("请先选择策略类型");

      // 1. Save investment profile so SimGuard can lock on hasProfile.
      //    Best-effort — failure here should not block session creation.
      try { await saveProfile(); } catch { /* profile save is best-effort */ }

      // 2. Create the AI trading session (also creates sim_account),
      //    and persist sectors/risk/board in session.params for the AI worker.
      await createSession({
        strategyId: matched.id,
        fundAmount,
        params: JSON.stringify({ sectors: selectedSectors, risk: riskLevel, board: selectedBoard }),
      });

      setCreatedOk(true);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "创建失败");
    }
  }, [strategies, selectedStrategyId, fundAmount, selectedSectors, riskLevel, selectedBoard, saveProfile]);

  const toggleSector = (s: string) => {
    if (s === "不限制") {
      setSelectedSectors(["不限制"]);
      return;
    }
    if (selectedSectors.includes("不限制")) {
      setSelectedSectors([s]);
      return;
    }
    if (selectedSectors.includes(s)) {
      setSelectedSectors(selectedSectors.filter((x) => x !== s));
    } else {
      setSelectedSectors([...selectedSectors, s]);
    }
  };

  const startScan = async () => {
    if (!selectedStrategyId) {
      setCreateError("请先选择策略类型");
      return;
    }
    if (simStatus && !simStatus.addonActive) {
      setCreateError("");
      try {
        await saveProfile();
        setShowLock(true);
      } catch (e) {
        setCreateError(e instanceof Error ? e.message : "投资偏好保存失败");
      }
      return;
    }
    setIsScanning(true);
    setScanProgress(0);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setScanProgress(i);
      if (i >= scanSteps.length) {
        clearInterval(interval);
      }
    }, 600);
    runCreate();
  };

  const progressPercent = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* ── Progress Bar ── */}
      <div className="px-6 pt-8 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
            className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-foreground-600" />
          </button>
          <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-foreground-400">{step}/3</span>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          {step === 1 ? "设定模拟资金" : step === 2 ? "选择投资方向" : "确认启动配置"}
        </h2>
        <p className="text-xs text-foreground-500 mt-0.5">虚拟资金，随时可改</p>
      </div>

      <div className="flex-1 px-6">
        {/* ══════════════════════════════════════════════════════════════
            Step 1: Fund Amount
           ══════════════════════════════════════════════════════════════ */}
        {step === 1 && !isScanning && (
          <div>
            <div className="text-center mb-8 mt-4">
              <p className="text-xs text-foreground-400 mb-2">模拟资金</p>
              {fundAmount > 0 ? (
                <h1 className="text-4xl font-extrabold text-foreground-950">
                  ¥{fundAmount.toLocaleString("zh-CN")}
                </h1>
              ) : (
                <h1 className="text-4xl font-extrabold text-foreground-300">¥0</h1>
              )}
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">自定义金额</label>
              <input
                type="number"
                value={customFund}
                onChange={(e) => {
                  setCustomFund(e.target.value);
                  const v = parseFloat(e.target.value);
                  if (v > 0) setFundAmount(v);
                }}
                placeholder="输入金额"
                className="w-full bg-white border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-950 outline-none focus:border-primary-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {fundOptions.map((f) => (
                <button
                  key={f}
                  onClick={() => { setFundAmount(f); setCustomFund(""); }}
                  className={`py-4 rounded-xl text-center cursor-pointer transition-all ${
                    fundAmount === f && !customFund
                      ? "bg-primary-500 text-white shadow-md shadow-primary-200"
                      : "bg-white text-foreground-700 border border-background-200"
                  }`}
                >
                  <p className="text-xs opacity-70">快选</p>
                  <p className="text-lg font-bold whitespace-nowrap">
                    ¥{(f / 10000).toFixed(0)}万
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            Step 2: Sectors + Risk + Knowledge
           ══════════════════════════════════════════════════════════════ */}
        {step === 2 && !isScanning && (
          <div className="mt-4">
            <p className="text-sm font-semibold text-foreground-950 mb-3">策略类型（单选）</p>
            <div className="space-y-3 mb-6">
              {strategies.length === 0 ? (
                <div className="rounded-xl bg-white p-4 text-center text-xs text-foreground-400">策略加载中...</div>
              ) : strategies.map((strategy) => {
                const selected = strategy.id === selectedStrategyId;
                return (
                  <button
                    key={strategy.id}
                    type="button"
                    onClick={() => setSelectedStrategyId(strategy.id)}
                    className={`w-full rounded-xl border-2 bg-white p-4 text-left transition-all ${
                      selected ? "border-primary-500 shadow-sm" : "border-background-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{strategy.name}</p>
                        <p className="mt-1 text-xs text-foreground-500">{strategy.description}</p>
                      </div>
                      <i className={`${selected ? "ri-checkbox-circle-fill text-primary-500" : "ri-checkbox-blank-circle-line text-foreground-300"} text-xl`} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-foreground-500">
                      <span className="rounded-full bg-background-100 px-2 py-1">{strategy.style || riskOf(strategy.maxDrawdown)}</span>
                      <span className="rounded-full bg-background-100 px-2 py-1">年化 {strategy.annualReturn.toFixed(1)}%</span>
                      <span className="rounded-full bg-background-100 px-2 py-1">最大回撤 {Math.abs(strategy.maxDrawdown).toFixed(1)}%</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-sm font-semibold text-foreground-950 mb-3">关注板块（多选）</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {sectorOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleSector(s)}
                  className={`text-sm px-4 py-2 rounded-full cursor-pointer transition-all ${
                    selectedSectors.includes(s)
                      ? "bg-primary-500 text-white"
                      : "bg-white text-foreground-600 border border-background-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-foreground-950 mb-3">市场板块（单选）</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {boardOptions.map((b) => (
                <button
                  key={b}
                  onClick={() => setSelectedBoard(b)}
                  className={`text-sm px-4 py-2 rounded-full cursor-pointer transition-all ${
                    selectedBoard === b
                      ? "bg-primary-500 text-white shadow-sm"
                      : "bg-white text-foreground-600 border border-background-200"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-foreground-950 mb-3">风险偏好</p>
            <div className="space-y-3 mb-6">
              {riskOptions.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRiskLevel(r.value)}
                  className={`w-full bg-white rounded-xl p-4 text-left cursor-pointer transition-all border-2 ${
                    riskLevel === r.value ? "border-current" : "border-transparent"
                  }`}
                  style={riskLevel === r.value ? { borderColor: r.color } : {}}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-base font-bold" style={{ color: r.color }}>{r.label}</span>
                    {riskLevel === r.value && (
                      <i className="ri-checkbox-circle-fill text-sm" style={{ color: r.color }} />
                    )}
                  </div>
                  <p className="text-xs text-foreground-500">{r.description}</p>
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-foreground-950 mb-3">投资经验</p>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                { value: "beginner", label: "新手", desc: "刚接触投资" },
                { value: "intermediate", label: "进阶", desc: "有一定经验" },
                { value: "advanced", label: "老手", desc: "经验丰富" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setKnowledgeLevel(opt.value)}
                  className={`rounded-xl p-3 text-center cursor-pointer transition-all border-2 ${
                    knowledgeLevel === opt.value
                      ? "bg-primary-50 border-primary-300 text-primary-700"
                      : "bg-white border-background-200 text-foreground-600"
                  }`}
                >
                  <p className="text-sm font-semibold">{opt.label}</p>
                  <p className="text-[10px] text-foreground-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            Step 3: Confirm
           ══════════════════════════════════════════════════════════════ */}
        {step === 3 && !isScanning && (
          <div className="mt-4">
            <div className="bg-white rounded-2xl p-5 mb-6">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">配置摘要</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-foreground-500">模拟资金</span>
                  <span className="text-sm font-bold text-foreground-950">
                    ¥{fundAmount.toLocaleString("zh-CN")}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-sm text-foreground-500">策略类型</span>
                  <span className="text-right text-sm font-semibold text-foreground-950">
                    {strategies.find((strategy) => strategy.id === selectedStrategyId)?.name || "未选择"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-foreground-500">市场板块</span>
                  <span className="text-sm text-foreground-700">{selectedBoard || "不限制"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-foreground-500">关注板块</span>
                  <span className="text-sm text-foreground-700">{selectedSectors.length ? selectedSectors.join(" · ") : "不限制"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-foreground-500">风险偏好</span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: riskOptions.find((r) => r.value === riskLevel)?.color }}
                  >
                    {riskOptions.find((r) => r.value === riskLevel)?.label}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-foreground-500">投资经验</span>
                  <span className="text-sm text-foreground-700">
                    {{ beginner: "新手", intermediate: "进阶", advanced: "老手" }[knowledgeLevel]}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={startScan}
              className="w-full py-4 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl text-base font-bold cursor-pointer whitespace-nowrap"
            >
              {simStatus && !simStatus.addonActive ? "提交偏好并申请开通" : "开始 AI 初始化"}
            </button>
            {createError && <p className="mt-3 text-center text-xs text-red-500">{createError}</p>}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            Scanning Animation
           ══════════════════════════════════════════════════════════════ */}
        {isScanning && (
          <div className="mt-6">
            <div className="text-center mb-8">
              <p className="text-lg font-bold text-foreground-950 mb-1">AI 引擎启动中</p>
              <p className="text-xs text-foreground-500">正在初始化你的模拟盘环境...</p>
            </div>

            <div className="bg-white rounded-2xl p-6">
              <div className="space-y-4">
                {scanSteps.map((s, i) => (
                  <div key={s} className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        i < scanProgress
                          ? "bg-green-100"
                          : i === scanProgress
                          ? "bg-primary-100 animate-pulse"
                          : "bg-background-100"
                      }`}
                    >
                      {i < scanProgress ? (
                        <i className="ri-check-line text-green-500 text-sm" />
                      ) : i === scanProgress ? (
                        <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="text-xs text-foreground-300">{i + 1}</span>
                      )}
                    </div>
                    <span
                      className={`text-sm ${
                        i < scanProgress
                          ? "text-foreground-700"
                          : i === scanProgress
                          ? "text-primary-600 font-medium"
                          : "text-foreground-300"
                      }`}
                    >
                      {s.replace(" ✓", "")}
                    </span>
                  </div>
                ))}
              </div>

              {scanProgress >= scanSteps.length && createError && (
                <div className="mt-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-error-warning-line text-red-500 text-2xl" />
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 mb-1">初始化失败</p>
                  <p className="text-xs text-foreground-500 mb-4">{createError}</p>
                  <button
                    onClick={runCreate}
                    className="w-full py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    重试
                  </button>
                </div>
              )}

              {scanProgress >= scanSteps.length && !createError && !createdOk && (
                <div className="mt-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 mb-4">正在创建模拟盘...</p>
                </div>
              )}

              {scanProgress >= scanSteps.length && createdOk && (
                <div className="mt-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-double-line text-green-500 text-2xl" />
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 mb-1">全部初始化完成</p>
                  <p className="text-xs text-foreground-500 mb-4">模拟盘已启动，AI 正在为你盯盘</p>
                  <button
                    onClick={() => navigate("/")}
                    className="w-full py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    进入 AI 看盘
                  </button>
                  <button
                    onClick={() => navigate("/ai-strategy")}
                    className="w-full py-3 mt-2 bg-white border border-background-200 text-foreground-600 rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    查看 AI 策略
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Bottom Nav for Step 1/2 ── */}
      {!isScanning && step < 3 && (
        <div className="px-6 pb-8 pt-4">
          <button
            onClick={() => setStep(step + 1)}
            disabled={(step === 1 && fundAmount <= 0) || (step === 2 && selectedStrategyId <= 0)}
            className={`w-full py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
              (step === 1 && fundAmount <= 0) || (step === 2 && selectedStrategyId <= 0)
                ? "bg-background-200 text-foreground-400 pointer-events-none"
                : "bg-gradient-to-r from-primary-500 to-accent-500 text-white"
            }`}
          >
            下一步
          </button>
        </div>
      )}

      {showLock && (
        <SimLockOverlay
          userId={simStatus?.userId}
          addonExpired={simStatus?.addonExpired}
          onBack={() => {
            setShowLock(false);
            navigate("/");
          }}
        />
      )}
    </div>
  );
}
